import React from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/i18n/LanguageContext";
import Reveal from "@/components/site/Reveal";
import { RevealTextInstant } from "@/components/site/RevealText";
import { CAD_STACK, CAPABILITIES } from "@/lib/data";

const PORTRAIT = "https://images.pexels.com/photos/3861938/pexels-photo-3861938.jpeg?auto=compress&cs=tinysrgb&w=1400";
const STUDIO_IMG = "https://images.unsplash.com/photo-1581092580497-e0d23cbdf1dc?auto=format&fit=crop&w=1600&q=80";

const CAPABILITY_LABEL_IT = {
  "complex-assemblies": "Complessivi complessi",
  "manufacturing-drawings": "Tavole di produzione",
  "welded-structures": "Strutture saldate",
  "sheet-metal": "Carpenteria in lamiera",
  "reverse-engineering": "Reverse engineering",
  "prototypes": "Prototipi",
  "product-development": "Sviluppo prodotto",
  "bim-modeling": "Progettazione BIM",
  "canopies-walkways": "Tettoie e passerelle",
};
const CAPABILITY_LABEL_EN = {
  "complex-assemblies": "Complex assemblies",
  "manufacturing-drawings": "Manufacturing drawings",
  "welded-structures": "Welded structures",
  "sheet-metal": "Sheet metal",
  "reverse-engineering": "Reverse engineering",
  "prototypes": "Prototypes",
  "product-development": "Product development",
  "bim-modeling": "BIM design",
  "canopies-walkways": "Canopies & walkways",
};

export default function AboutPage() {
  const { t, lang } = useLang();
  const capMap = lang === "it" ? CAPABILITY_LABEL_IT : CAPABILITY_LABEL_EN;

  return (
    <div data-testid="page-about" className="pt-32 md:pt-40">
      <section className="mx-auto max-w-[1440px] px-6 md:px-10 pb-24 md:pb-32">
        <div className="grid grid-cols-12 gap-6 mb-12">
          <div className="col-span-12 md:col-span-3 az-tag"><span className="az-tag__dot" /><span>{t.about.kicker}</span></div>
          <div className="col-span-12 md:col-span-3 md:col-start-10 md:text-right font-mono text-[10px] tracking-widest uppercase text-[#86868B]">
            Antonio Zaccaria · IT
          </div>
        </div>
        <RevealTextInstant
          as="h1"
          lines={[t.about.title]}
          className="font-serif tracking-tighter leading-[0.92] text-[13vw] md:text-[9vw] lg:text-[8rem]"
          lineClassName="block"
        />
        <div className="mt-12 grid grid-cols-12 gap-8">
          <div className="col-span-12 md:col-span-6 md:col-start-7">
            <Reveal delay={0.2}>
              <p className="font-sans text-xl md:text-2xl leading-relaxed text-[#0A0A0A] max-w-[52ch]">
                {t.about.lead}
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="relative bg-[#F5F5F7] border-y border-[#E5E5EA] py-24 md:py-40">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10 grid grid-cols-12 gap-8">
          <div className="col-span-12 md:col-span-5">
            <div className="relative aspect-[4/5] overflow-hidden bg-[#0A0A0A]">
              <img src={PORTRAIT} alt="Engineering studio work" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
              <div className="absolute bottom-4 left-4 right-4 flex justify-between text-white font-mono text-[10px] tracking-widest uppercase">
                <span>{t.common.founder}</span>
                <span>{t.common.role}</span>
              </div>
            </div>
          </div>
          <div className="col-span-12 md:col-span-6 md:col-start-7 flex flex-col gap-8 md:gap-12">
            <Reveal><p className="font-serif text-2xl md:text-4xl leading-[1.15] tracking-tight">{t.about.bio1}</p></Reveal>
            <Reveal delay={0.1}><p className="font-sans text-base md:text-lg leading-relaxed text-[#86868B]">{t.about.bio2}</p></Reveal>
            <Reveal delay={0.15}><p className="font-sans text-base md:text-lg leading-relaxed text-[#86868B]">{t.about.bio3}</p></Reveal>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-40 bg-white">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10">
          <div className="az-tag mb-8"><span className="az-tag__dot" /><span>{t.about.valuesKicker}</span></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#E5E5EA] border border-[#E5E5EA]">
            {t.about.values.map((v, i) => (
              <Reveal key={v.t} delay={i * 0.05} className="bg-white">
                <div className="p-10 md:p-14 min-h-[240px] flex flex-col justify-between transition-colors duration-500 hover:bg-[#F5F5F7]">
                  <span className="font-mono text-[10px] tracking-widest uppercase text-[#86868B]">0{i + 1}</span>
                  <div>
                    <h3 className="font-serif text-3xl md:text-4xl tracking-tight">{v.t}</h3>
                    <p className="mt-4 font-sans text-[15px] md:text-base text-[#86868B] max-w-[46ch] leading-relaxed">{v.d}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-40 bg-[#0A0A0A] text-white">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10 grid grid-cols-12 gap-8">
          <div className="col-span-12 md:col-span-4">
            <div className="az-tag mb-6" style={{ color: "#FFFFFF" }}><span className="az-tag__dot" /><span>{t.about.stackKicker}</span></div>
            <Reveal>
              <div className="space-y-3 md:space-y-4">
                {CAD_STACK.map((s) => (
                  <div key={s} className="font-serif text-4xl md:text-5xl tracking-tight leading-none">
                    {s}<span className="text-[#0055FF]">.</span>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
          <div className="col-span-12 md:col-span-7 md:col-start-6">
            <div className="az-tag mb-6" style={{ color: "#FFFFFF" }}><span className="az-tag__dot" /><span>{t.about.capabilitiesKicker}</span></div>
            <div className="grid grid-cols-2 gap-y-4">
              {CAPABILITIES.map((c, i) => (
                <Reveal key={c} delay={i * 0.03} className="border-t border-white/15 pt-4">
                  <span className="font-mono text-[10px] tracking-widest text-white/50 block mb-2">{String(i + 1).padStart(2, "0")}</span>
                  <span className="font-serif text-xl md:text-2xl">{capMap[c]}</span>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-40 bg-white border-t border-[#E5E5EA]" data-testid="credentials-section">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10 grid grid-cols-12 gap-8">
          <div className="col-span-12 md:col-span-4">
            <div className="az-tag mb-6"><span className="az-tag__dot" /><span>{t.about.credentialsKicker}</span></div>
            <Reveal>
              <h2 className="font-serif text-4xl md:text-5xl leading-[1.02] tracking-tighter max-w-[16ch]">
                ISO 9001<span className="text-[#0055FF]">.</span> BIM<span className="text-[#0055FF]">.</span>
              </h2>
            </Reveal>
          </div>
          <div className="col-span-12 md:col-span-7 md:col-start-6">
            <div className="grid grid-cols-1 gap-px bg-[#E5E5EA] border border-[#E5E5EA]">
              {t.about.credentials.map((c, i) => (
                <Reveal key={c.t} delay={i * 0.05} className="bg-white">
                  <div className="p-8 md:p-10 flex flex-col md:flex-row md:items-baseline gap-4 md:gap-8">
                    <div className="md:w-40 shrink-0">
                      <span className="font-mono text-[10px] tracking-widest uppercase text-[#86868B]">0{i + 1}</span>
                      <h3 className="mt-2 font-serif text-2xl md:text-3xl tracking-tight">{c.t}</h3>
                    </div>
                    <p className="font-sans text-[15px] md:text-base text-[#86868B] leading-relaxed max-w-[52ch]">{c.d}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-40 bg-[#F5F5F7] border-t border-[#E5E5EA]" data-testid="network-section">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10 grid grid-cols-12 gap-8">
          <div className="col-span-12 md:col-span-4">
            <div className="az-tag mb-6"><span className="az-tag__dot" /><span>{t.about.networkKicker}</span></div>
            <div className="mt-4 font-mono text-[11px] tracking-widest uppercase text-[#0055FF]">Design → Production</div>
          </div>
          <div className="col-span-12 md:col-span-7 md:col-start-6">
            <Reveal>
              <h2 className="font-serif text-4xl md:text-6xl leading-[0.98] tracking-tighter max-w-[22ch]">
                {t.about.networkTitle}
              </h2>
            </Reveal>
            <Reveal delay={0.08}>
              <p className="mt-8 font-sans text-lg md:text-xl leading-relaxed text-[#86868B] max-w-[52ch]">
                {t.about.networkBody}
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-white text-center">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10 flex flex-col items-center gap-8">
          <Reveal>
            <p className="font-serif text-3xl md:text-5xl tracking-tight leading-tight max-w-[24ch] mx-auto">
              {t.home.contactTitle}
            </p>
          </Reveal>
          <Link to="/contact" data-testid="about-contact-cta" className="az-btn">
            <span>{t.nav.cta}</span>
            <span className="az-btn__arrow" aria-hidden="true">→</span>
          </Link>
        </div>
      </section>
    </div>
  );
}
